import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { Send, CheckCircle, XCircle, AlertCircle, Loader } from 'lucide-react';

function getAuthHeader() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const STATUS_CONFIG = {
  added: {
    icon: CheckCircle,
    color: 'text-emerald-600',
    bg: 'bg-emerald-50 border-emerald-200',
  },
  already_exists: {
    icon: CheckCircle,
    color: 'text-blue-600',
    bg: 'bg-blue-50 border-blue-200',
  },
  not_qualified: {
    icon: XCircle,
    color: 'text-amber-600',
    bg: 'bg-amber-50 border-amber-200',
  },
  login_required: {
    icon: AlertCircle,
    color: 'text-blue-600',
    bg: 'bg-blue-50 border-blue-200',
  },
  error: {
    icon: AlertCircle,
    color: 'text-red-600',
    bg: 'bg-red-50 border-red-200',
  },
};

export default function AnalystSubmitForm() {
  const [handle, setHandle] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Accept full URLs like https://x.com/handle or twitter.com/handle
    const urlMatch = handle.trim().match(/(?:twitter\.com|x\.com)\/([A-Za-z0-9_]{1,50})/);
    const cleaned = urlMatch ? urlMatch[1] : handle.trim().replace(/^@/, '');
    if (!cleaned) return;

    setLoading(true);
    setResult(null);

    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/research/submit-analyst/`,
        { twitter_handle: cleaned },
        { headers: getAuthHeader() }
      );
      setResult(res.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        setResult({ status: 'login_required' });
      } else if (err.response?.status === 429) {
        setResult({
          status: 'error',
          message: 'Too many submissions. Please wait an hour before trying again.',
        });
      } else {
        setResult({
          status: 'error',
          message:
            err.response?.data?.error ||
            err.response?.data?.message ||
            'Something went wrong. Please try again.',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const config = result ? STATUS_CONFIG[result.status] ?? STATUS_CONFIG.error : null;
  const StatusIcon = config?.icon;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <p className="text-sm font-medium text-gray-700 mb-3">
        Know an analyst we should track?
      </p>

      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          type="text"
          value={handle}
          onChange={(e) => setHandle(e.target.value)}
          placeholder="@handle or https://x.com/handle"
          disabled={loading}
          className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={loading || !handle.trim()}
          className="flex items-center gap-1.5 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? (
            <Loader size={14} className="animate-spin" />
          ) : (
            <Send size={14} />
          )}
          Submit
        </button>
      </form>

      {result && (
        <div className={`mt-3 flex items-start gap-2 px-3 py-2.5 rounded-lg border text-sm ${config.bg}`}>
          <StatusIcon size={15} className={`mt-0.5 flex-shrink-0 ${config.color}`} />
          <span className="text-gray-700">
            {result.status === 'login_required' ? (
              <>
                To use this feature you need to{' '}
                <a href="/login" className="underline font-medium">
                  log in
                </a>
                .
              </>
            ) : (
              <>
                {result.message}
                {result.analyst_slug && result.status === 'already_exists' && (
                  <>
                    {' '}
                    <a
                      href={`/analysts/${result.analyst_slug}`}
                      className="underline font-medium"
                    >
                      View profile
                    </a>
                  </>
                )}
              </>
            )}
          </span>
        </div>
      )}
    </div>
  );
}
